"use client"

import React, { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import AppButton from "@/app/components/atomic/molecules/AppButton"
import AppIcon from "@/app/components/atomic/AppIcon"
import QuestionLabel from "@/app/components/atomic/molecules/QuestionLabel"
import {
    MOTIFS_WITHOUT_TRIP_AMOUNT,
    TravelMotif,
    useTravelComparatorStore,
} from "@/app/stores/travel/travel-comparator.store"
import { useTracking } from "@/app/contexts/tracking-context"
import { TravelTrackingEvents } from "@/app/constants/travelTracking"
import {
    ADULT_AGE,
    ageFromIsoBirthdate,
    isDestinationAllowedFor,
    MAX_TRAVELER_AGE,
    MISSING_TRAVEL_FIELD_LABELS,
    missingTravelFields,
    today,
} from "../travelFormConfig"
import { travelStepUrl } from "../travelFunnelSteps"
import TravelMotifField from "./TravelMotifField"
import TravelTripFields from "./TravelTripFields"
import { TravelLandingPreset } from "./travelLandingPresets"

const CANCELLATION_HELP =
    "Rembourse les frais de voyage engagés si vous devez annuler avant le départ (maladie, accident, imprévu)."

export default function TravelLandingForm({ preset }: { preset?: TravelLandingPreset }) {
    const router = useRouter()
    const { form, patchForm, startLandingComparison, error } = useTravelComparatorStore()
    const { track } = useTracking()
    const [submitting, setSubmitting] = useState(false)
    const [wantsCancellation, setWantsCancellation] = useState(false)
    const [showMissingFields, setShowMissingFields] = useState(false)
    // Visite fraîche → état vierge ; retour depuis « Modifier ma simulation » (store hydraté) → on
    // conserve la simulation et on ré-coche l'annulation si un montant avait été renseigné.
    useEffect(() => {
        const state = useTravelComparatorStore.getState()
        if (!state.hydrated) {
            state.reset()
            state.patchForm(preset ?? {})
            setWantsCancellation(false)
        } else {
            setWantsCancellation(state.form.tripAmount !== "")
        }
        track({ event: TravelTrackingEvents.funnelAccess })
    }, [track, preset])

    // Règles d'éligibilité communes : âges plausibles, porteur du contrat majeur, voyage non commencé.
    const eligibilityError = useMemo(() => {
        const ages = form.travelers.map((traveler) => ageFromIsoBirthdate(traveler.birthdate))
        if (ages.some((age) => age !== null && (age < 0 || age > MAX_TRAVELER_AGE))) {
            return `Merci d'indiquer un âge compris entre 0 et ${MAX_TRAVELER_AGE} ans pour chaque voyageur.`
        }
        const mainInsuredAge = ages[0] ?? null
        if (mainInsuredAge !== null && mainInsuredAge < ADULT_AGE) {
            return "L'assuré principal doit être majeur (18 ans ou plus)."
        }
        if (form.startDate !== "" && form.startDate < today()) {
            return "La date de départ ne peut pas être dans le passé."
        }
        return null
    }, [form.travelers, form.startDate])

    const isVolunteer = form.motif === "volontariat"

    const missingFields = missingTravelFields(form, wantsCancellation)
    const missingFieldsMessage =
        showMissingFields && missingFields.length > 0
            ? `Pour voir les offres, indiquez ${missingFields.map((field) => MISSING_TRAVEL_FIELD_LABELS[field]).join(", ")}.`
            : null

    const toggleCancellation = (checked: boolean) => {
        setWantsCancellation(checked)
        if (!checked) {
            patchForm({ tripAmount: "" })
        }
    }

    // Un motif sans annulation à couvrir (volontariat) masque et réinitialise le montant du voyage ;
    // une destination fermée au nouveau motif (PVT) est réinitialisée pour éviter une saisie morte.
    const changeMotif = (motif: TravelMotif) => {
        const destinationCountries = form.destinationCountries.filter((code) => isDestinationAllowedFor(motif, code))
        if (MOTIFS_WITHOUT_TRIP_AMOUNT.includes(motif)) {
            setWantsCancellation(false)
            patchForm({ motif, destinationCountries, tripAmount: "" })
            return
        }
        patchForm({ motif, destinationCountries })
    }

    return (
        <form
            onSubmit={async (event) => {
                event.preventDefault()
                if (submitting || eligibilityError) {
                    return
                }
                if (missingFields.length > 0) {
                    setShowMissingFields(true)
                    return
                }
                setSubmitting(true)
                track({ event: TravelTrackingEvents.submitTravelInfo })
                const token = await startLandingComparison()
                if (token) {
                    router.push(travelStepUrl(token, "offres"))
                } else {
                    setSubmitting(false)
                }
            }}
            className="flex flex-col gap-5 rounded-[20px] bg-white p-[18px] shadow-[0_4px_20px_rgba(0,0,0,0.06)] sm:rounded-3xl sm:p-7"
            data-test="travel-landing-card"
            data-quote-form
        >
            <div className="flex items-center justify-between gap-3">
                <h2 className="block px-0 text-[21px] leading-tight sm:text-2xl">Ma comparaison gratuite</h2>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-pastel-yellow px-3 py-1.5 text-[13px] font-bold text-pale">
                    <AppIcon iconClassName="fa-clock" size="sm" />
                    30 secondes
                </span>
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={1}>
                    <span className="sm:hidden">Quel type de séjour ?</span>
                    <span className="hidden sm:inline">Quel type de séjour préparez-vous ?</span>
                </QuestionLabel>
                <TravelMotifField value={form.motif} onChange={changeMotif} />
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={2}>Votre voyage</QuestionLabel>
                <TravelTripFields />
            </div>

            {isVolunteer ? (
                <div className="flex flex-col gap-1 rounded-[14px] border border-rm-gray-light bg-white px-3.5 py-3 transition-colors focus-within:border-rm-blue">
                    <label htmlFor="travel-organisme" className="text-xs font-bold uppercase tracking-wide text-rm-gray-dark">
                        Organisme d&apos;accueil
                    </label>
                    <input
                        id="travel-organisme"
                        value={form.organisme}
                        onChange={(event) => patchForm({ organisme: event.target.value })}
                        placeholder="Association, ONG…"
                        data-test="travel-organisme"
                        className="w-full bg-transparent text-[15px] text-pale placeholder:text-[#9AA0AC] focus:outline-none"
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-3 rounded-[14px] bg-rm-cream px-4 py-3.5">
                    <label className="flex cursor-pointer items-center gap-3">
                        <input
                            type="checkbox"
                            checked={wantsCancellation}
                            onChange={(event) => toggleCancellation(event.target.checked)}
                            data-test="travel-cancellation"
                            className="peer sr-only"
                        />
                        <span
                            aria-hidden="true"
                            className="flex size-[22px] shrink-0 items-center justify-center rounded-md border-2 border-[#DFD5D1] bg-white transition-colors peer-checked:border-rm-blue peer-checked:bg-rm-blue peer-focus-visible:ring-2 peer-focus-visible:ring-rm-blue peer-focus-visible:ring-offset-1"
                        >
                            {wantsCancellation && <AppIcon iconClassName="fa-check" size="xs" className="text-white" />}
                        </span>
                        <span className="flex-1 text-sm text-pale sm:text-[15px]">
                            Je souhaite souscrire une assurance annulation
                        </span>
                        <span
                            title={CANCELLATION_HELP}
                            aria-label={CANCELLATION_HELP}
                            className="hidden size-5 shrink-0 items-center justify-center rounded-full bg-[#4a5565] text-[11px] font-bold text-white sm:flex"
                        >
                            i
                        </span>
                    </label>

                    {wantsCancellation && (
                        <div className="relative">
                            <input
                                inputMode="numeric"
                                value={form.tripAmount}
                                onChange={(event) => patchForm({ tripAmount: event.target.value.replace(/[^\d]/g, "") })}
                                placeholder="Montant du voyage par personne"
                                aria-label="Montant du voyage par personne"
                                className="h-[52px] w-full rounded-xl border border-rm-gray-light bg-white pl-4 pr-12 text-[15px] text-pale placeholder:text-[#9AA0AC] focus:border-rm-blue focus:outline-none"
                            />
                            <span className="pointer-events-none absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg bg-neutral text-sm font-bold text-pale">
                                €
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col items-center gap-3">
                <AppButton
                    type="submit"
                    theme="orange"
                    arrowPosition="before"
                    wFull
                    disabled={submitting || eligibilityError !== null}
                    loading={submitting}
                    dataTest="travel-landing-submit"
                >
                    Obtenir mon tarif
                </AppButton>

                <span className="flex items-center gap-2 text-center text-xs text-[#71717A] sm:text-[13px]">
                    <AppIcon iconClassName="fa-lock" size="xs" />
                    <span>
                        Gratuit, sans engagement, souscription immédiate<span className="hidden sm:inline"> en ligne</span>
                    </span>
                </span>
            </div>

            {(eligibilityError || missingFieldsMessage || error) && (
                <p role="alert" className="text-[13px] text-red-600">
                    {eligibilityError ?? missingFieldsMessage ?? error}
                </p>
            )}
        </form>
    )
}
